import PropTypes from 'prop-types';
import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
} from 'react';
import { fetchUserAttributes } from 'aws-amplify/auth';
import { useLocation } from 'react-router-dom';

import CustomSnackbar from '../components/shared/molecules/CustomSnackbar';
import {
  getLastSelectedLocationCache,
  getUserCache,
  getZoomCache,
  logout,
  removeUserCache,
  setLastSelectedLocationCache,
  setUserCache,
  setZoomCache,
} from '../axios/axiosAuth';
import Loader from '../components/shared/molecules/Loader';
import { useUserCustomization } from '../hooks/useUserCustomization';
import { useFavicon } from '../hooks/useFavicon';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const GeneralContext = createContext();

const Provider = ({ children }) => {
  const { pathname } = useLocation();
  const [authenticatedUser, setAuthenticatedUser] = useState(getUserCache());
  const [showPredictive, setShowPredictive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lastSelectedLocation, setLastSelectedLocation] = useState(
    getLastSelectedLocationCache(),
  );
  const [clickedAsset, setClickedAsset] = useState(null);
  const [openBuildings, setOpenBuildings] = useState([]);
  const [buildingsExpanded, setBuildingsExpanded] = useState(true);
  const [assetsExpanded, setAssetsExpanded] = useState(true);
  const [zoom, setZoom] = useState(getZoomCache() || 6);
  const [snackbars, setSnackbars] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [sideMenuForm, setSideMenuForm] = useState(false);
  const [menuExpanded, setMenuExpanded] = useState(true);
  const [pairedDevice, setPairedDevice] = useState();
  const [customizationId, setCustomizationId] = useState();

  const { data: customization } = useUserCustomization({
    email: authenticatedUser?.email,
    customizationId,
  });

  useFavicon(customization?.favicon);
  useDocumentTitle(customization?.tab_title);

  const isMapPage = useMemo(
    () =>
      pathname.includes('/portal/maps') && !pathname.includes('/facilities'),
    [pathname],
  );

  const openToast = (message, severity = 'info') => {
    setSnackbars(prev => [
      ...prev,
      { id: new Date().getTime(), message, severity },
    ]);
  };
  const closeToast = id => setSnackbars(prev => prev.filter(s => s.id !== id));

  const setUser = user => {
    setUserCache(user);
    setAuthenticatedUser(user);
  };

  const resetUser = () => {
    removeUserCache();
    setAuthenticatedUser(null);
  };

  const setLastSelectedLoc = location => {
    setLastSelectedLocationCache(location);
    setLastSelectedLocation(location);
  };

  const setMapZoom = zoom => {
    setZoomCache(zoom);
    setZoom(zoom);
  };

  const values = useMemo(
    () => ({
      user: authenticatedUser,
      setUser,
      resetUser,
      openToast,
      closeToast,
      showPredictive,
      setShowPredictive,
      loading,
      setLoading,
      setLastSelectedLocation: setLastSelectedLoc,
      lastSelectedLocation,
      setMapZoom,
      zoom,
      reportData,
      setReportData,
      sideMenuForm,
      setSideMenuForm,
      customization,
      setCustomizationId,
      pairedDevice,
      setPairedDevice,
      menuExpanded,
      setMenuExpanded,
      openBuildings,
      setOpenBuildings,
      buildingsExpanded,
      setBuildingsExpanded,
      assetsExpanded,
      setAssetsExpanded,
      isMapPage,
      clickedAsset,
      setClickedAsset,
    }),
    [
      authenticatedUser,
      showPredictive,
      loading,
      lastSelectedLocation,
      zoom,
      reportData,
      sideMenuForm,
      customization,
      pairedDevice,
      menuExpanded,
      openBuildings,
      buildingsExpanded,
      assetsExpanded,
      isMapPage,
      clickedAsset,
    ],
  );

  useEffect(() => {
    const loadUser = async () => {
      try {
        setLoading(true);
        const user = await fetchUserAttributes();
        setUser(user);
      } catch (error) {
        console.error(error);
        logout(resetUser);
      } finally {
        setLoading(false);
      }
    };
    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <GeneralContext.Provider value={values}>
        {children}
      </GeneralContext.Provider>
      <Loader open={loading} />
      {snackbars.map((snack, index) => (
        <CustomSnackbar
          key={snack.id}
          index={snackbars.length - 1 - index}
          data-testid="toast"
          severity={snack.severity}
          message={snack.message}
          onSnackbarClose={(event, reason) => {
            if (reason === 'clickaway') return;
            closeToast(snack.id);
          }}
        />
      ))}
    </>
  );
};

export const useGeneralContext = () => useContext(GeneralContext);

Provider.propTypes = {
  children: PropTypes.element.isRequired,
};

export default Provider;
